import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import HelpTooltip from '../../components/UI/HelpTooltip';
import { emailSettingsAPI } from '../../services/api';
import { Mail, Save, Send, Shield, Check } from 'lucide-react';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const SectionCard = styled(Card)`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const IconWrapper = styled.div`
  width: 50px;
  height: 50px;
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.primary}20;
  color: ${props => props.theme.colors.primary};
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
`;

const Message = styled.div`
  padding: 0.9rem 1.2rem;
  border-radius: ${props => props.theme.borderRadius.md};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  border: 1px solid
    ${props =>
      props.type === 'error'
        ? props.theme.colors.error + '50'
        : props.theme.colors.success + '50'};
  background-color:
    ${props =>
      props.type === 'error'
        ? props.theme.colors.error + '10'
        : props.theme.colors.success + '10'};
  color:
    ${props =>
      props.type === 'error'
        ? props.theme.colors.error
        : props.theme.colors.success};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border.medium};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  background-color: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.text.primary};
  transition: all 0.2s ease-in-out;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary + '20'};
  }
`;

const SelectLabel = styled.label`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  display: block;
  margin-bottom: 0.5rem;
`;

const EmailSettingsPage = () => {
  const { theme } = useTheme();
  const [settings, setSettings] = useState({
    mailer: 'smtp',
    host: '',
    port: 587,
    username: '',
    password: '',
    encryption: 'ssl',
    from_address: '',
    from_name: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await emailSettingsAPI.getSettings();
      if (response.data) {
        const data = response.data;
        setSettings({
          mailer: data.mailer || 'smtp',
          host: data.host || '',
          port: data.port || 587,
          username: data.username || '',
          password: data.password || '',
          encryption: data.encryption || 'none',
          from_address: data.from_address || '',
          from_name: data.from_name || '',
        });
      }
    } catch (error) {
      console.error('Failed to fetch email settings:', error);
      setMessage({
        type: 'error',
        text:
          error.response?.data?.message ||
          'Failed to load email settings. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      const payload = {
        ...settings,
        port: Number(settings.port) || 0,
        encryption: settings.encryption || 'none',
      };
      const response = await emailSettingsAPI.updateSettings(payload);
      if (response.data?.settings) {
        const updated = response.data.settings;
        setSettings(prev => ({
          ...prev,
          ...updated,
          encryption: updated.encryption || 'none',
        }));
      }
      setMessage({
        type: 'success',
        text: response.data?.message || 'Email settings updated successfully.',
      });
    } catch (error) {
      console.error('Failed to save email settings:', error);
      setMessage({
        type: 'error',
        text:
          error.response?.data?.message ||
          'Failed to save email settings. Please review the values and try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      setMessage({
        type: 'error',
        text: 'Please enter a test email address.',
      });
      return;
    }

    try {
      setTestLoading(true);
      setMessage(null);
      const response = await emailSettingsAPI.sendTestEmail({ email: testEmail });
      setMessage({
        type: 'success',
        text: response.data?.message || 'Test email sent successfully.',
      });
    } catch (error) {
      console.error('Failed to send test email:', error);
      setMessage({
        type: 'error',
        text:
          error.response?.data?.message ||
          'Failed to send test email. Please verify the configuration.',
      });
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <PageContainer theme={theme}>
      {message && (
        <Message theme={theme} type={message.type}>
          {message.type === 'error' ? <Shield size={18} /> : <Check size={18} />}
          {message.text}
        </Message>
      )}

      <SectionCard theme={theme}>
        <SectionHeader>
          <IconWrapper theme={theme}>
            <Mail size={24} />
          </IconWrapper>
          <SectionTitle theme={theme}>
            SMTP Server Configuration
            <HelpTooltip
              steps={[
                {
                  title: 'Step 1 – Enter server details',
                  content: (
                    <ul>
                      <li>Fill in the SMTP host, port, username and password given by your email provider.</li>
                      <li>Choose the correct encryption type (usually SSL or TLS) that matches the provider.</li>
                    </ul>
                  ),
                },
                {
                  title: 'Step 2 – Set From address',
                  content: (
                    <ul>
                      <li>Use a professional address such as <strong>noreply@yourdomain.org</strong> for system emails.</li>
                      <li>The “From name” is what recipients see as the sender (for example, “EWHRDN Network”).</li>
                    </ul>
                  ),
                },
                {
                  title: 'Step 3 – Test delivery',
                  content: (
                    <p>
                      After saving, use the “Send Test Email” section below to confirm that messages reach your inbox.
                    </p>
                  ),
                },
              ]}
            />
          </SectionTitle>
        </SectionHeader>

        <Grid>
          <div>
            <SelectLabel theme={theme}>Mailer</SelectLabel>
            <Select
              theme={theme}
              value={settings.mailer}
              onChange={e => handleChange('mailer', e.target.value)}
              disabled
            >
              <option value="smtp">SMTP</option>
            </Select>
          </div>

          <Input
            label="SMTP Host"
            value={settings.host}
            onChange={e => handleChange('host', e.target.value)}
            placeholder="smtp.example.com"
            disabled={loading}
          />

          <Input
            label="SMTP Port"
            value={settings.port}
            onChange={e => handleChange('port', e.target.value)}
            placeholder="465"
            disabled={loading}
            type="number"
          />

          <div>
            <SelectLabel theme={theme}>Encryption</SelectLabel>
            <Select
              theme={theme}
              value={settings.encryption || 'none'}
              onChange={e => handleChange('encryption', e.target.value)}
              disabled={loading}
            >
              <option value="none">None</option>
              <option value="ssl">SSL</option>
              <option value="tls">TLS</option>
              <option value="starttls">STARTTLS</option>
            </Select>
          </div>

          <Input
            label="Username"
            value={settings.username}
            onChange={e => handleChange('username', e.target.value)}
            placeholder="Email account username"
            disabled={loading}
          />

          <Input
            label="Password"
            value={settings.password}
            onChange={e => handleChange('password', e.target.value)}
            placeholder="Email account password"
            type="password"
            disabled={loading}
          />
        </Grid>

        <Grid>
          <Input
            label="From Address"
            value={settings.from_address}
            onChange={e => handleChange('from_address', e.target.value)}
            placeholder="noreply@example.com"
            disabled={loading}
          />

          <Input
            label="From Name"
            value={settings.from_name}
            onChange={e => handleChange('from_name', e.target.value)}
            placeholder="Organization Name"
            disabled={loading}
          />
        </Grid>

        <ButtonGroup>
          <Button
            variant="primary"
            onClick={handleSave}
            leftIcon={<Save size={16} />}
            disabled={saving || loading}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </ButtonGroup>
      </SectionCard>

      <SectionCard theme={theme}>
        <SectionHeader>
          <IconWrapper theme={theme}>
            <Send size={24} />
          </IconWrapper>
          <SectionTitle theme={theme}>Send Test Email</SectionTitle>
        </SectionHeader>

        <Grid>
          <Input
            label="Test Email Address"
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            placeholder="you@example.com"
            disabled={testLoading}
          />
        </Grid>

        <ButtonGroup>
          <Button
            variant="secondary"
            onClick={handleSendTest}
            leftIcon={<Send size={16} />}
            disabled={testLoading}
          >
            {testLoading ? 'Sending...' : 'Send Test Email'}
          </Button>
        </ButtonGroup>
      </SectionCard>
    </PageContainer>
  );
};

export default EmailSettingsPage;

